import dayjs from 'dayjs';

import { DateFormat } from 'utils/date';

export const parseDecimal = (val: string | number): string => {
  const numericValue = Number(val);
  return String(numericValue.toFixed(numericValue % 1 === 0 ? 0 : 2));
};

/**
 * Billing-interval suffix for a price string: `/mo` for monthly plans,
 * `/every N units` otherwise, or an empty string when the interval is unknown.
 */
export const formatSubscriptionInterval = (paymentInterval?: {
  qty?: number;
  units?: string;
}): string => {
  const { qty, units } = paymentInterval ?? {};

  if (qty === 1 && units === 'months') return '/mo';
  if (qty && units) return `/every ${qty} ${units}`;

  return '';
};

/**
 * Shared "Next bill: $Y on MMM DD" caption rendered by both the billing program
 * card and the multi-select cancellation modal.
 */
export const formatNextBillCaption = (amount: string | number, at?: string): string =>
  `Next bill: $${parseDecimal(amount)} on ${dayjs(at).format(DateFormat.MMM_DD)}`;

export const formatPaymentMethod = (cardNumber?: string, cardType?: string) => {
  if (!cardNumber && !cardType) {
    return 'none';
  }
  if (cardNumber) {
    switch (cardNumber[0]) {
      case '3':
        // 4 and 7 are numbers for the Amex card
        if (['4', '7'].includes(cardNumber[1])) {
          return 'american_express';
        }
        return 'none';
      case '4':
        return 'visa';
      case '5':
        return 'master_card';
      case '6':
        return 'discover';

      default:
        return 'none';
    }
  } else {
    switch (cardType?.toLowerCase()) {
      case 'visa':
        return 'visa';
      case 'discover':
        return 'discover';
      case 'master card':
      case 'master':
        return 'master_card';
      case 'american_express':
        return 'american_express';
      default:
        return 'none';
    }
  }
};

export const getPaymentNumbers = (
  totalPrice?: string,
  discount?: string,
  creditBalance?: string,
  planCost?: string | number,
  isLifeMDPlusFlow?: boolean
) => {
  const numTotalPrice = Number(totalPrice);
  if (numTotalPrice === 0) {
    // if price is 0, we should skip !numTotalPrice condition
  } else if (!numTotalPrice) {
    return {
      creditUsed: '',
      discountValue: '',
      finalPrice: ''
    };
  }
  const discountValue = isLifeMDPlusFlow
    ? parseFloat(discount || '0')
    : !discount
      ? 0
      : (numTotalPrice * parseFloat(discount)) / 100;
  const creditUsed = !creditBalance
    ? 0
    : isLifeMDPlusFlow
      ? Math.min(numTotalPrice + Number(planCost || 0), parseFloat(creditBalance))
      : Math.min(numTotalPrice - discountValue, parseFloat(creditBalance));

  const finalPrice = isLifeMDPlusFlow
    ? Math.max(numTotalPrice + Number(planCost || 0) - parseFloat(creditBalance || '0'), 0)
    : Math.max(numTotalPrice - discountValue - parseFloat(creditBalance || '0'), 0);
  return {
    creditUsed: parseDecimal(creditUsed ?? 0),
    discountValue: parseDecimal(discountValue ?? 0),
    // if price has decimal, show it. If no, hide .00
    finalPrice: parseDecimal(finalPrice)
  };
};
